/* eslint-disable no-unused-vars */
import FileAttachment from "./shared/FileAttachment";
import styles from "../../styles/forms/SharedForm.module.css";
import SplitFormLayout from "../shared/SplitFormLayout";
import { safeLocalStorageGet } from "../../utils/safeJsonParse";
import { getSubmitterInfo, getDefaultCountry } from "../../utils/formHelpers";

import { useFormState } from "../../hooks/useFormState";
import { useFormSubmit } from "../../hooks/useFormSubmit";
import {
  FormSection,
  FormField,
  PinnedSubmit,
  SuccessCard,
} from "./shared/FormComponents";
import { FormProvider } from "./shared/FormContext";
import T, { useTranslate } from "../T";

const STEPS = [
  { id: "details", label: "Suggestion Details", desc: "Title & Type" },
  { id: "impact", label: "Business Impact", desc: "What & Why" },
  { id: "attachments", label: "Attachments", desc: "Relevant files" },
];

const SUGGESTION_TYPES = [
  "UI/UX Improvement",
  "Process Improvement",
  "Performance Optimization",
  "Cost Saving Suggestion",
  "Customer Experience Idea",
  "Minor Change Request",
  "Documentation",
  "Other",
];

const INITIAL = {
  title: "",
  type: "",
  suggestion: "",
  emailCCs: "",
  attachments: [],
};

export default function SuggestionForm({ onBack }) {
  const { tText } = useTranslate();
  const {
    formData,
    handleChange,
    handleAddFiles,
    handleRemoveFile,
    setAllTouched,
    contextValue,
  } = useFormState(INITIAL);

  const { submitForm, isSubmitting, submitted, error } = useFormSubmit();

  const handleBack =
    onBack || (() => (window.location.href = "/staff/request/general"));

  const onSubmit = () => {
    const profile = safeLocalStorageGet("staff_profile");
    const { submittedBy, creatorName } = getSubmitterInfo(profile);
    const country = getDefaultCountry(profile);

    submitForm(formData, (attachments) => {
      const nocobasePayload = {
        user_name: creatorName,
        category: 1,
        Summary: formData.title,
        description: formData.suggestion,
        impactedsystem: [],
        priority: "Low",
        environment: "Production",
        loanid: "",
        carid: "",
        dealerid: "",
        Description: `Suggestion Type: ${formData.type}\n\n${formData.suggestion}`,
        Country: country,
        sourceofrequest: "Staff Portal",
        confluencelink: "",
        accesstype: "",
        accesslevel: "",
        linemanagername: "",
        linemanageremail: "",
        mailinglist: "",
        status: "Open",
        tickettype: "General Inquiry/ System Enhancement",
        Resolution: "",
        rootcause: "",
        f_7ygfivdcnao: "",
        Creator: creatorName,
        Creatoremail: submittedBy,
        attachement: attachments.map(a => a.id),
      };

      return { nocobasePayload };
    });
  };

  if (submitted) {
    return (
      <SplitFormLayout
        badge="General Request"
        title="Suggestion"
        subtitle="Submit your ideas for process or system improvements."
        steps={STEPS.map(step => ({ ...step, label: tText(step.label), desc: tText(step.desc) }))}
        currentStep={STEPS.length}
        middleBreadcrumb={{ label: tText("General"), path: "/staff/request/general" }}
        currentBreadcrumb="Suggestion"
      >
        <SuccessCard
          title="Suggestion Submitted"
          description="Thank you for your suggestion! Our team will review it and assess its business impact."
          onBack={handleBack}
          backLabel="Back to General"
        />
      </SplitFormLayout>
    );
  }

  const formValid = formData.title && formData.type && formData.suggestion;

  return (
    <SplitFormLayout
      badge="General Request"
      title="Suggestion"
      subtitle="Submit your ideas for process or system improvements."
      steps={STEPS.map(step => ({ ...step, label: tText(step.label), desc: tText(step.desc) }))}
      middleBreadcrumb={{ label: tText("General"), path: "/staff/request/general" }}
      currentBreadcrumb="Suggestion"
    >
      <FormProvider value={contextValue}>
        <form
          onSubmit={(e) => e.preventDefault()}
          className={styles.ticketFormWrapper}
        >
          {error && <div className={styles.errorBanner}>{tText(error)}</div>}

          <FormSection id="details" title="1. Suggestion Details">
            <FormField
              label="Short Title"
              name="title"
              required
              style={{ marginBottom: "20px" }}
            >
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                className={styles.input}
                placeholder={tText("e.g. Add dark mode to portal")}
                required
              />
            </FormField>
            <FormField label="Suggestion Type(s)" name="type" required>
              <select
                name="type"
                value={formData.type}
                onChange={handleChange}
                className={styles.select}
                required
              >
                <option value="">{tText("Select...")}</option>
                {SUGGESTION_TYPES.map((t) => (
                  <option key={t} value={t}>
                    <T>{t}</T>
                  </option>
                ))}
              </select>
            </FormField>
          </FormSection>

          <FormSection id="impact" title="2. Impact & Description">
            <FormField
              label="What’s your suggestion and why?"
              name="suggestion"
              required
              style={{ marginBottom: "20px" }}
            >
              <textarea
                name="suggestion"
                value={formData.suggestion}
                onChange={handleChange}
                className={styles.textarea}
                rows={6}
                placeholder={tText("Problem Statement \\ Solution \\ Expected Business Impact")}
                required
              />
            </FormField>
          </FormSection>

          <FormSection id="attachments" title="3. Attachments">
            <FileAttachment
              attachments={formData.attachments}
              onChange={handleAddFiles}
              onRemove={handleRemoveFile}
            />
          </FormSection>

          <PinnedSubmit
            isSubmitting={isSubmitting}
            isValid={formValid}
            onSubmit={onSubmit}
            onInvalidSubmit={setAllTouched}
            label="Submit Suggestion"
          />
        </form>
      </FormProvider>
    </SplitFormLayout>
  );
}
